/**
 * Server-driven runtime feature flags, client side. Today: the AI assistant
 * (Reinsight) kill switch. The BACKEND is the enforcement point (every
 * /ai-assistant route answers 403 AI_ASSISTANT_DISABLED while
 * AI_ASSISTANT_ENABLED is not "true"); this module only decides WHICH
 * assistant the shell mounts, so a dark environment never flashes an
 * assistant that would only error.
 *
 * TRI-STATE, on purpose. `null` = the backend has not answered yet (initial
 * load, or the fetch failed) → App.tsx mounts nothing; `true` → Reinsight;
 * `false` → the legacy /ai/command widget (LegacyAiCommandWidget), i.e. exactly
 * what production showed before Reinsight. Three states rather than a boolean
 * because a boolean default would have to pick one widget to flash for the
 * moment before /public/auth-config returns and then swap it — the null state
 * is what keeps that swap from ever being visible.
 *
 * Same design rules as sessionPolicy: dependency-free (plain subscribe/notify +
 * React's useSyncExternalStore), unknown-until-told — never on-until-told-
 * otherwise. Deliberately NOT persisted: a flag that outlived a flip in the
 * other direction would show the wrong assistant for a whole session.
 */
import { useSyncExternalStore } from 'react';

let aiAssistantEnabled: boolean | null = null;
const listeners = new Set<() => void>();

export function getAiAssistantEnabled(): boolean | null {
  return aiAssistantEnabled;
}

/**
 * Set from GET /public/auth-config (AuthProvider) once the server has answered.
 * Only the boolean `true` turns Reinsight on; anything else the server sends
 * (false, a missing field, a string) means "answered: off" → legacy widget.
 * Callers that did NOT get an answer (fetch failed) must not call this — leave
 * the flag null so nothing mounts until the next wake retries.
 */
export function setAiAssistantEnabled(value: unknown): void {
  const next = value === true;
  if (next === aiAssistantEnabled) return;
  aiAssistantEnabled = next;
  listeners.forEach((fn) => fn());
}

/** Back to "not yet known" (tests; nothing mounts until the server answers again). */
export function resetAiAssistantEnabled(): void {
  if (aiAssistantEnabled === null) return;
  aiAssistantEnabled = null;
  listeners.forEach((fn) => fn());
}

export function subscribeFeatureFlags(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reactive read — re-renders the caller when the flag flips. Server snapshot: unknown. */
export function useAiAssistantEnabled(): boolean | null {
  return useSyncExternalStore(subscribeFeatureFlags, getAiAssistantEnabled, () => null);
}
